import { useEffect, useState } from "react"
import _ from "lodash"
import moment from "moment"

import { CUTOFF_TIME, DAY_START_TIME, TIME_STEP_MINUTES } from "./static"

const aMinutos = hora => {
  const partes = String(hora || "00:00").split(":")
  return (Number(partes[0]) || 0) * 60 + (Number(partes[1]) || 0)
}

const aHora = minutos =>
  `${String(Math.floor(minutos / 60)).padStart(2, "0")}:${String(
    minutos % 60
  ).padStart(2, "0")}`

/**
 * Día base de la recepción: la fecha del pedido que se está capturando. Solo
 * cae en hoy mientras no hay cliente elegido y la pantalla aún no tiene fecha.
 */
const diaBase = fechaPedido => {
  const dia = moment(fechaPedido, "YYYY-MM-DD", true)
  return dia.isValid() ? dia : moment()
}

/** «Hoy»/«Mañana» solo si la opción cae de verdad en el día real. */
const prefijoFecha = (valor, dias) => {
  if (valor === moment().format("YYYY-MM-DD")) return "Hoy"
  if (valor === moment().add(1, "days").format("YYYY-MM-DD")) return "Mañana"
  return dias === 0 ? "Mismo día" : "Día siguiente"
}

/**
 * Solo se puede recibir el día del pedido o el siguiente, igual que el
 * calendario anterior, pero contando desde la fecha del pedido y no desde hoy.
 */
export const opcionesFecha = fechaPedido => {
  const base = diaBase(fechaPedido)
  return [0, 1].map(dias => {
    const dia = moment(base).add(dias, "days")
    const value = dia.format("YYYY-MM-DD")
    return {
      value,
      label: `${prefijoFecha(value, dias)} — ${_.capitalize(
        dia.format("ddd DD MMM")
      )}`,
    }
  })
}

/**
 * El corte de las 19:00 solo aplica a la entrega con flete el día del pedido.
 * Si se recoge en granja no hay ruta que respetar: el horario se abre desde la
 * hora actual cuando el pedido es de hoy y desde las 00:00 cualquier otro día,
 * donde «la hora actual» no significa nada. El día siguiente siempre libre.
 */
export const horaMinima = (fecha, esLocal, fechaPedido) => {
  const base = diaBase(fechaPedido)
  const siguiente = moment(base).add(1, "days").format("YYYY-MM-DD")
  if (fecha === siguiente) return DAY_START_TIME
  if (!esLocal) return CUTOFF_TIME

  const esHoy = base.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD")
  return esHoy ? moment().format("HH:mm") : DAY_START_TIME
}

export const opcionesHora = (fecha, esLocal, fechaPedido) => {
  const minimo = aMinutos(horaMinima(fecha, esLocal, fechaPedido))
  const inicio = Math.ceil(minimo / TIME_STEP_MINUTES) * TIME_STEP_MINUTES

  const opciones = []
  for (let minutos = inicio; minutos < 24 * 60; minutos += TIME_STEP_MINUTES) {
    opciones.push({ value: aHora(minutos), label: `${aHora(minutos)} hrs` })
  }
  return opciones
}

/** Precio final que verá el cliente para una fila, según se entregue o se recoja. */
export const precioDeFila = (precio, esLocal) =>
  esLocal ? precio.unit_price_local : precio.unit_price_shipped

/**
 * Borrador del pedido de UN proveedor. Vive en la tarjeta del proveedor y se
 * reinicia cuando cambian sus filas (fecha, zona o cliente distintos).
 *
 * `fechaPedido` es el día de disponibilidad elegido arriba: sobre él se calcula
 * la recepción, que es lo que viaja al servidor como `timeDelivery`.
 */
export default function useProviderOrderForm(rows, regionId, fechaPedido) {
  const [cantidades, setCantidades] = useState({})
  const [priceSelected, setPriceSelected] = useState({})
  const [precios, setPrecios] = useState({})
  const [preciosLocal, setPreciosLocal] = useState({})
  const [observaciones, setObservaciones] = useState({})
  const [pickLocal, setPickLocal] = useState(false)
  const [fechaRecepcion, setFechaRecepcion] = useState(
    () => opcionesFecha(fechaPedido)[0].value
  )
  const [horaRecepcion, setHoraRecepcion] = useState("")
  const [error, setError] = useState(null)

  useEffect(() => {
    const preciosInit = {}
    const preciosLocalInit = {}
    const seleccionInit = {}

    ;(rows || []).forEach(fila => {
      preciosInit[fila.id] = {}
      preciosLocalInit[fila.id] = {}

      // Con una sola zona no hay nada que elegir: se marca sola.
      if ((fila.prices || []).length === 1) {
        seleccionInit[fila.id] = fila.prices[0].id
      }

      ;(fila.prices || []).forEach(precio => {
        preciosInit[fila.id][precio.id] = precio.unit_price_shipped
        preciosLocalInit[fila.id][precio.id] = precio.unit_price_local
      })
    })

    setPrecios(preciosInit)
    setPreciosLocal(preciosLocalInit)
    setPriceSelected(seleccionInit)
    setCantidades({})
    setObservaciones({})
    setError(null)
  }, [rows])

  // Al cambiar la fecha del pedido cambia la base de la recepción: se vuelve al
  // día del pedido y se vacía la hora, cuyo mínimo ya no es el mismo.
  useEffect(() => {
    setFechaRecepcion(opcionesFecha(fechaPedido)[0].value)
    setHoraRecepcion("")
  }, [fechaPedido])

  const horas = opcionesHora(fechaRecepcion, pickLocal, fechaPedido)

  const ajustarHora = (fecha, esLocal) => {
    const minimo = horaMinima(fecha, esLocal, fechaPedido)
    // Al apretar el mínimo, la hora ya elegida puede quedar fuera de rango.
    setHoraRecepcion(previo => (previo && previo < minimo ? "" : previo))
  }

  const setCantidad = (filaId, valor) =>
    setCantidades(previo => ({ ...previo, [filaId]: valor }))

  const setObservacion = (filaId, valor) =>
    setObservaciones(previo => ({ ...previo, [filaId]: valor }))

  const setPrecioSeleccionado = (filaId, precioId) =>
    setPriceSelected(previo => ({ ...previo, [filaId]: precioId }))

  const setPrecio = (filaId, precioId, valor) => {
    const numero = parseFloat(Number(valor).toFixed(2))
    if (pickLocal) {
      setPreciosLocal(previo => ({
        ...previo,
        [filaId]: { ...previo[filaId], [precioId]: numero },
      }))
      return
    }
    setPrecios(previo => ({
      ...previo,
      [filaId]: { ...previo[filaId], [precioId]: numero },
    }))
  }

  const togglePickLocal = valor => {
    setPickLocal(valor)
    ajustarHora(fechaRecepcion, valor)
  }

  const onChangeFecha = valor => {
    setFechaRecepcion(valor)
    ajustarHora(valor, pickLocal)
  }

  const reset = () => {
    setCantidades({})
    setObservaciones({})
    setError(null)
  }

  const pedidas = Object.keys(cantidades).filter(
    filaId => parseInt(cantidades[filaId], 10) > 0
  )

  /** Devuelve el mensaje de error, o null si el borrador es válido. */
  const validar = () => {
    if (pedidas.length === 0)
      return "Debes capturar la cantidad de al menos un producto."

    const sinPrecio = pedidas.some(filaId => priceSelected[filaId] == null)
    if (sinPrecio) return "Debes seleccionar un precio para el producto."

    if (!fechaRecepcion || !horaRecepcion)
      return "Debes colocar un horario de entrega"

    return null
  }

  const construirPayload = order => ({
    provider_id: rows[0].provider.id,
    farm_id: rows[0].farm.id,
    orders: pedidas.reduce(
      (acumulado, filaId) => ({
        ...acumulado,
        [filaId]: {
          value: cantidades[filaId],
          local: pickLocal,
          region_id: regionId,
        },
      }),
      {}
    ),
    prices: priceSelected,
    prices_amount: pickLocal ? preciosLocal : precios,
    observations: observaciones,
    pickLocal,
    timeDelivery: `${fechaRecepcion} ${horaRecepcion}`,
    order_id: order ? order.id : null,
  })

  return {
    state: {
      cantidades,
      priceSelected,
      precios,
      preciosLocal,
      observaciones,
      pickLocal,
      fechaRecepcion,
      horaRecepcion,
      fechas: opcionesFecha(fechaPedido),
      horas,
    },
    error,
    setError,
    events: {
      setCantidad,
      setObservacion,
      setPrecioSeleccionado,
      setPrecio,
      togglePickLocal,
      onChangeFecha,
      onChangeHora: setHoraRecepcion,
      reset,
    },
    validar,
    construirPayload,
  }
}
